import type { PickByValue } from 'utility-types'

import type { ReadonlyNonEmptyArray } from '../../../../utils'

import type { CybusServiceForm, CybusServiceRawParameters, Translation } from '../../../../domain'

import type { ServiceManagementMode, Usecases } from '../../../../application'

import { createExtendedState } from '../../utils'

/**
 * Services can be interacted with in various ways
 * This config object facilitates communication between the react components
 */
export type ModalConfig = Readonly<{
    title: Translation
    configurationTitle: Translation
    dismissalButtonText: Translation
    confirmationButtonText: Translation | null
    noConfigurationText: Translation

    /**
     * If parameters/fields/file can be edited
     */
    isEditable: boolean
    hasNumberedSteps: boolean
    shouldShowUploadInstructions: boolean

    usecase: keyof PickByValue<
        Usecases,
        {
            getSupportedServiceCommissioningFileExtensions(): ReadonlyNonEmptyArray<string>

            /**
             * Called when the file changes
             */
            updateCommissioningFile(file: CybusServiceForm['file']): void

            getServiceManagementMode(): ServiceManagementMode

            /**
             * Called when user cancels the process
             */
            cancel(): void

            /**
             * Called when user confirms the process
             */
            confirm(): Promise<string | null>

            /**
             * When the input parameters change
             */
            updateServiceParameters(id: CybusServiceForm['id'], newParameters: CybusServiceRawParameters | null): void

            /**
             * Called when the validity of the given form parameters need to be checked
             */
            isConfirmationRequestValid(): boolean
        }
    >

    onConfirmed?: (id: string) => void
}>

const { Provider: ModalConfigProvider, useExtendedState } = createExtendedState<ModalConfig>({ ignorePropsChanges: true })

export const useModalConfig = <K extends keyof ModalConfig>(k: K): ModalConfig[K] => useExtendedState((s) => s[k])

export { ModalConfigProvider }
